/**
 * The option lists the calendar's master-data filters offer (R11.2, R11.3, R11.4).
 *
 * ## What this replaces
 *
 * These dimensions previously rendered as free-text identifier inputs, on the
 * stated grounds that "the API does not yet expose an option source to a calendar
 * reader". That is no longer the case: `GET /api/master-data/:set/selectable` is
 * gated on `initiative:read`, which every calendar reader holds, and
 * `GET /api/users/selectable` covers Owner. So the filters can offer named values,
 * which is what UI spec §3.6 and the click-dummy reference both show, and what
 * R11.2 requires ("offering the active Master_Data_Administration records of each
 * dimension").
 *
 * ## R11.2's inactive-but-referenced records
 *
 * R11.2 also requires any *inactive* record referenced by an Initiative of the
 * selected planning year to be offered, and R11.15 requires a Saved_View's
 * deactivated value to stay applied and named. `includeIds` is the mechanism, and
 * this module passes the ids the query currently holds - which covers the
 * Saved_View case of R11.15 and any value the reader has selected.
 *
 * It does **not** yet cover a record that is inactive and referenced by an
 * Initiative of the year but *not* currently selected: that set is only knowable
 * server-side, and no endpoint reports it. Such a value is absent from the
 * dropdown until it is selected (for instance by applying a Saved_View that names
 * it), at which point it appears, labelled and flagged inactive. Closing that gap
 * needs the year-scoped referenced-id set on the calendar response.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  selectableUsersResponseSchema,
  type CalendarQuery,
  type MasterDataSet,
  type SalesCycle,
} from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';
import {
  REFERENCE_STALE_TIME_MS,
  toNameLookup,
  useSelectableMasterDataSets,
  type SelectableRecord,
} from '@/shared/api/selectable-master-data';
import type { ListFilterKey } from '@/shared/url/calendar-search-params';
import type { LabelResolver } from './filter-chips';

/** One selectable value a filter control offers. */
export interface FilterOption {
  readonly value: string;
  readonly label: string;
  /** True for a value retained only because it is referenced (R11.2, R11.15). */
  readonly isInactive: boolean;
}

/**
 * Which master data set backs each master-data filter dimension.
 *
 * `ownerUserIds` is deliberately absent: Owner is an account, read from
 * `/api/users/selectable`, not a master data set.
 */
export const MASTER_DATA_SET_BY_FILTER_KEY = {
  platformIds: 'platform',
  channelIds: 'channel',
  customerGroupIds: 'customer-group',
  audienceIds: 'audience',
  categoryIds: 'category',
  initiativeTypeIds: 'initiative-type',
  priorityIds: 'priority',
} as const satisfies Partial<Record<ListFilterKey, MasterDataSet>>;

/** The filter dimensions this module supplies options for. */
export type MasterDataFilterKey = keyof typeof MASTER_DATA_SET_BY_FILTER_KEY;

export const MASTER_DATA_FILTER_KEYS = Object.keys(
  MASTER_DATA_SET_BY_FILTER_KEY,
) as readonly MasterDataFilterKey[];

const MASTER_DATA_SETS: readonly MasterDataSet[] = Object.values(MASTER_DATA_SET_BY_FILTER_KEY);

/** What {@link useCalendarFilterOptions} returns. */
export interface CalendarFilterOptions {
  /** Options per master-data dimension, plus `ownerUserIds` and `salesCycleIds`. */
  readonly optionsByKey: ReadonlyMap<ListFilterKey, readonly FilterOption[]>;
  /**
   * Resolves a dimension's applied id to its display name, for the chip row (UI
   * spec §3.7). Keyed by dimension (not a single flat id -> name map) so the chip
   * row's `deriveChips(query, count, resolveLabel, t)` call - which always passes
   * both the dimension key and the value - resolves against the right option
   * list: `resolveLabel('salesCycleIds', id)` and `resolveLabel('platformIds', id)`
   * look up their own dimension's records rather than colliding on a merged
   * id space.
   */
  readonly resolveLabel: LabelResolver;
  readonly isLoading: boolean;
  /**
   * True when at least one option list failed.
   *
   * Surfaced rather than swallowed: a filter whose options failed to load looks
   * identical to a dimension with no configured values, and the reader would
   * conclude the data is missing rather than the request.
   */
  readonly isError: boolean;
}

function toOptions(records: readonly SelectableRecord[] | undefined): readonly FilterOption[] {
  return (records ?? [])
    .map((record) => ({
      value: record.id,
      label: record.name,
      isInactive: !record.isActive,
    }))
    // Active values lead, so the live choices are the ones a reader meets first;
    // a retained inactive value sits at the end, marked.
    .sort((a, b) => Number(a.isInactive) - Number(b.isInactive) || a.label.localeCompare(b.label));
}

/**
 * Loads every master-data option list the filter bar needs, plus the assignable
 * Owner accounts.
 *
 * `query` is read for the ids currently applied, which become each set's
 * `includeIds` - see this module's note on R11.2.
 *
 * `salesCycles` is passed in rather than fetched here: `CalendarPage` already
 * holds the year's Sales_Cycle definitions via `useSalesCycles` (for the grid's
 * Sales_Cycle orientation and the filter bar's own dropdown), and that call is
 * cached by planning year - reusing it here is one request, not two, and it is
 * what makes `salesCycleIds` resolvable to a name at all, since Sales_Cycle is
 * not a `MasterDataSet` and `useSelectableMasterDataSets` never sees it.
 */
export function useCalendarFilterOptions(
  query: CalendarQuery,
  salesCycles: readonly SalesCycle[] = [],
): CalendarFilterOptions {
  const includeIdsBySet = useMemo(() => {
    const bySet: Partial<Record<MasterDataSet, readonly string[]>> = {};
    for (const key of MASTER_DATA_FILTER_KEYS) {
      bySet[MASTER_DATA_SET_BY_FILTER_KEY[key]] = query[key].map(String);
    }
    return bySet;
  }, [query]);

  const sets = useSelectableMasterDataSets(MASTER_DATA_SETS, { includeIdsBySet });

  const ownerIds = useMemo(() => query.ownerUserIds.map(String), [query.ownerUserIds]);
  const owners = useQuery({
    queryKey: queryKeys.selectableUsers(ownerIds),
    queryFn: async () => {
      const body = await apiFetch<unknown>(
        ownerIds.length === 0
          ? '/api/users/selectable'
          : `/api/users/selectable?${new URLSearchParams({ includeIds: ownerIds.join(',') }).toString()}`,
      );
      return selectableUsersResponseSchema.parse(body).items;
    },
    staleTime: REFERENCE_STALE_TIME_MS,
  });

  // Sales_Cycle options, sorted the same way every other dimension's are
  // (active first, then alphabetical) - `salesCycles` only ever carries the
  // active definitions of the year (see `fetchSalesCycles`), so there is no
  // inactive-but-referenced case to mark here the way R11.2/R11.15 need for the
  // master-data dimensions.
  const salesCycleOptions = useMemo<readonly FilterOption[]>(
    () =>
      salesCycles
        .map((cycle) => ({ value: cycle.id, label: cycle.name, isInactive: false }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [salesCycles],
  );

  const optionsByKey = useMemo(() => {
    const map = new Map<ListFilterKey, readonly FilterOption[]>();
    for (const key of MASTER_DATA_FILTER_KEYS) {
      map.set(key, toOptions(sets.bySet.get(MASTER_DATA_SET_BY_FILTER_KEY[key])));
    }
    map.set('salesCycleIds', salesCycleOptions);
    map.set(
      'ownerUserIds',
      (owners.data ?? [])
        .map((user) => ({
          value: user.id,
          label: user.displayName,
          isInactive: !user.isActive,
        }))
        .sort(
          (a, b) => Number(a.isInactive) - Number(b.isInactive) || a.label.localeCompare(b.label),
        ),
    );
    return map;
  }, [sets.bySet, owners.data, salesCycleOptions]);

  // One id -> name lookup per dimension, keyed the same way `optionsByKey` is,
  // so `resolveLabel(key, value)` looks a value up in *its own* dimension's
  // names rather than in one map merged across every dimension - two
  // master-data sets could otherwise collide on an id, and a dimension whose
  // records are not a `MasterDataSet` at all (Sales_Cycle, Owner) would have no
  // home in a single merged map to begin with.
  const lookupByKey = useMemo(() => {
    const map = new Map<ListFilterKey, ReadonlyMap<string, string>>();
    for (const key of MASTER_DATA_FILTER_KEYS) {
      map.set(key, toNameLookup(sets.bySet.get(MASTER_DATA_SET_BY_FILTER_KEY[key])));
    }
    map.set(
      'salesCycleIds',
      new Map(salesCycles.map((cycle) => [cycle.id, cycle.name] as const)),
    );
    map.set(
      'ownerUserIds',
      new Map((owners.data ?? []).map((user) => [user.id, user.displayName] as const)),
    );
    return map;
  }, [sets.bySet, salesCycles, owners.data]);

  const resolveLabel = useMemo<LabelResolver>(
    () => (key, value) => lookupByKey.get(key)?.get(value),
    [lookupByKey],
  );

  return {
    optionsByKey,
    resolveLabel,
    isLoading: sets.isLoading || owners.isLoading,
    isError: sets.isError || owners.isError,
  };
}
